<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Monitor Date</h1>
      <p class="subtitle">Set the next monitoring date for {{ patientName }}.</p>
    </div>

    <div class="modal-card-body">
      <div class="field">
        <label class="form-label">Next monitor date</label>
        <SelectInput
          :modelValue="selection"
          @update:modelValue="handleSelectionChange"
          :options="intervalOptions"
          placeholder="Select an interval..."
        />
      </div>

      <div v-if="selection === 'custom'" class="field">
        <label class="form-label">Custom date</label>
        <DateInput v-model="customDate" placeholder="MM/DD/YYYY" />
      </div>
    </div>

    <div class="modal-card-footer">
      <button v-if="patient.monitorDate" class="button form-button" :disabled="isSaving" @click="handleClear">
        Clear
      </button>
      <button class="button primary form-button" :disabled="!canSubmit" @click="handleSubmit">
        Save
      </button>
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import DateInput from '@/components/inputs/DateInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import * as api from '@/api';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';
import { addDays, getLocalToday } from '@/utils/dateUtils';
import * as formatUtils from '@/utils/formatters';

// Standard monitoring intervals, in days from today.
const INTERVAL_DAYS = [140, 116, 105, 100, 95, 87, 77, 70];

export default {
  name: 'SetMonitorDateModal',
  components: { CloseIcon, DateInput, SelectInput },
  props: {
    patient: {
      type: Object,
      required: true
    }
  },
  emits: ['saved'],
  data() {
    const today = getLocalToday();
    const existing = this.patient.monitorDate || '';
    // Pre-select the matching interval when the existing date is exactly one of
    // them; otherwise fall back to a custom date.
    const matchingDays = INTERVAL_DAYS.find((days) => addDays(today, days) === existing);

    return {
      today,
      selection: existing ? (matchingDays ? String(matchingDays) : 'custom') : null,
      customDate: matchingDays ? '' : existing,
      isSaving: false
    };
  },
  computed: {
    patientName() {
      return `${this.patient.firstName || ''} ${this.patient.lastName || ''}`.trim() || 'this patient';
    },
    intervalOptions() {
      return [
        ...INTERVAL_DAYS.map((days) => ({
          value: String(days),
          label: `${days} days from today`,
          subtext: formatUtils.formatDate(addDays(this.today, days))
        })),
        { value: 'custom', label: 'Custom...' }
      ];
    },
    monitorDate() {
      if (!this.selection) return '';
      if (this.selection === 'custom') return this.customDate || '';
      return addDays(this.today, Number(this.selection));
    },
    canSubmit() {
      return !!this.monitorDate && !this.isSaving;
    }
  },
  methods: {
    handleSelectionChange(value) {
      this.selection = value;
    },
    handleSubmit() {
      if (!this.canSubmit) return;
      this.save(this.monitorDate);
    },
    handleClear() {
      this.save(null);
    },
    async save(monitorDate) {
      this.isSaving = true;
      try {
        await api.setTrtMonitorDate(this.patient.patientId, monitorDate);
        queryClient.invalidateQueries({ queryKey: ['trt-cards'] });
        this.$emit('saved');
      } catch (err) {
        console.error('Failed to set monitor date:', err);
        alertService.error('Failed to set monitor date: ' + err.message);
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
}

.modal-card-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-card-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
